// Core packages

// Third party packages

// Custom packages

/**
 * Pure helpers of the dossier on the 3.0.0 archive page (`documentsV3`): what the page shows, and to
 * whom, by subscription plan and by role type. They are the rules the legacy dossier writes inline in
 * its template (`property-documents.page.html`), gathered here so the page template only reads the
 * result and the rules can be tested without Angular.
 *
 * ONLY WHAT THE PAGE PRINTS. Three rules of the legacy are deliberately NOT here, because they decide
 * which documents the archive holds, and that belongs to the back-end composing `/tree`:
 *  - on the free plans no document is an obligation;
 *  - on the PRIVACY plan, a role that is not back office sees the privacy documents only;
 *  - the per area restrictions of the guests (`propertiesPermission`).
 *
 * @since 3.0.0
 */

/** `subscription.key` of the free plans: the legacy hides the paid tools from both */
const FREE_PLAN_KEYS = ["FREE", "FREEBASIC"];

/**
 * Role types that read a dossier from OUTSIDE: an occasional visitor, a technician invited onto one
 * property. The legacy hides every tool and every action from them, the download excepted.
 */
const GUEST_ROLE_TYPES = ["temporary", "shared"];

/**
 * Who is reading the dossier, as far as the visibility rules care.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveViewer {
  /**
   * `subscription.key` of the property. `""` for a property with no plan, which the legacy treats
   * as a paid one. `null` while the property has not answered, or when it could not be read.
   */
  subscriptionKey: string | null;
  /** Type of the active role ("admin", "master", "temporary", ...), `""` when not known yet */
  roleType: string;
  /** `propertiesPermission.docsInserted` of the active role: a guest limited to the uploaded documents */
  docsInsertedOnly: boolean;
}

/**
 * What the page shows, element by element.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveVisibility {
  /** The actions menu of the card header, with the row filters and the two name lists */
  actionsMenu: boolean;
  /** The filters "mancanti" and "strutture eliminate" of that menu */
  advancedFilters: boolean;
  /** "Vedi le note" and the two name lists, on top of the role gates of their own */
  planTools: boolean;
  /** Legend of the row colours */
  legend: boolean;
  /** Total of the uploaded documents, under the side menu */
  counterTotal: boolean;
  /** Counters by band, percentage and progress bar */
  counterDetails: boolean;
  /** Numbers on the entries of the side menu */
  sideBadges: boolean;
  /** Every action on a document but the download: read and edit its data, delete, print, insert */
  documentActions: boolean;
}

/**
 * The three row filters of the actions menu, as the page opens.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveInitialFilters {
  showDocumentsNotVirtual: boolean;
  showDocumentsExpired: boolean;
  showDocumentsNotInserted: boolean;
}

/**
 * Whether the plan is one of the two free ones.
 *
 * @param subscriptionKey `subscription.key` of the property
 */
export function isFreeArchivePlan(subscriptionKey: string | null): boolean {
  return typeof subscriptionKey === "string" && FREE_PLAN_KEYS.indexOf(subscriptionKey) > -1;
}

/**
 * Whether a role type reads the dossier from outside (temporary, shared).
 *
 * @param roleType Type of the active role
 */
export function isGuestRoleType(roleType: string): boolean {
  return typeof roleType === "string" && GUEST_ROLE_TYPES.indexOf(roleType) > -1;
}

/**
 * Whether the active role is limited to the uploaded documents (`propertiesPermission.docsInserted`).
 *
 * Read off the role object the auth service publishes, which has no type of its own there: anything
 * that is not a role carrying the flag answers false.
 *
 * @param role The active role, as `AuthService.loggedUserRequestedRoleObj$` emits it
 */
export function docsInsertedOnlyOf(role: unknown): boolean {
  if (!role || typeof role !== "object") {
    return false;
  }
  const permission = (role as { propertiesPermission?: unknown }).propertiesPermission;
  if (!permission || typeof permission !== "object") {
    return false;
  }
  return !!(permission as { docsInserted?: unknown }).docsInserted;
}

/**
 * What the page shows to this viewer: the legacy rules, by plan and by role type.
 *
 * Free plans (FREE, FREEBASIC): no notes, no name lists, no filters on the missing documents and on
 * the removed structures, no legend, no counters but the total, no numbers on the side menu.
 *
 * Guests (temporary, shared), whatever the plan: no actions menu, no legend, no counters at all, no
 * numbers on the side menu, and no action on the documents but the download.
 *
 * ⚠️ A PLAN NOT KNOWN YET HIDES THE PAID TOOLS: the property and the archive are read in parallel, and
 * showing them until the property answers would flash them in front of a free customer. A property
 * that cannot be read keeps them hidden; the dossier itself stays readable.
 *
 * @param viewer Who is reading the dossier
 */
export function documentArchiveVisibility(viewer: DocumentArchiveViewer): DocumentArchiveVisibility {
  const guest = isGuestRoleType(viewer ? viewer.roleType : "");
  const subscriptionKey = viewer ? viewer.subscriptionKey : null;
  const paidTools = typeof subscriptionKey === "string" && !isFreeArchivePlan(subscriptionKey);

  return {
    actionsMenu: !guest,
    advancedFilters: paidTools,
    planTools: paidTools,
    legend: paidTools && !guest,
    counterTotal: !guest,
    counterDetails: paidTools && !guest,
    sideBadges: paidTools && !guest,
    documentActions: !guest,
  };
}

/**
 * The row filters the page opens with: the legacy defaults by role type.
 *
 * The guests cannot change them — the actions menu is hidden from them — so for them these are the
 * filters, not just the starting point:
 *  - shared: neither the expired documents nor the missing ones;
 *  - temporary: no expired documents, and no missing ones when the role is limited to the uploaded
 *    documents (`docsInserted`).
 *
 * @param viewer Who is reading the dossier
 */
export function documentArchiveInitialFilters(viewer: DocumentArchiveViewer): DocumentArchiveInitialFilters {
  const roleType = viewer && typeof viewer.roleType === "string" ? viewer.roleType : "";

  if (roleType === "shared") {
    return {
      showDocumentsNotVirtual: true,
      showDocumentsExpired: false,
      showDocumentsNotInserted: false,
    };
  }

  if (roleType === "temporary") {
    return {
      showDocumentsNotVirtual: true,
      showDocumentsExpired: false,
      showDocumentsNotInserted: !viewer.docsInsertedOnly,
    };
  }

  return {
    showDocumentsNotVirtual: true,
    showDocumentsExpired: true,
    showDocumentsNotInserted: true,
  };
}
